import {
    HashLocationStrategy,
    LocationStrategy,
    registerLocaleData,
} from "@angular/common";
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withFetch,
    withInterceptorsFromDi,
} from "@angular/common/http";
import localeEn from "@angular/common/locales/en";
import localeIt from "@angular/common/locales/it";
import localeBr from "@angular/common/locales/pt";
import { ApplicationConfig, LOCALE_ID } from "@angular/core";
import { provideRouter, withRouterConfig } from "@angular/router";
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalInterceptor,
    MsalService,
} from "@azure/msal-angular";
import { routes } from "./app.routes";
import {
    MSALGuardConfigFactory,
    MSALInstanceFactory,
    MSALInterceptorConfigFactory,
} from "./core/services/auth.config";
import { AgendaRepositoryImpl } from "./data/repositories/agenda/agenda-impl.repository";
import { RealCostRepositoryImpl } from "./data/repositories/real-cost/real-cost-impl.repository";
import { AgreementRepositoryImpl } from "./data/repositories/agreement/agreement-impl.repository";
import { GlobalSearchRepositoryImpl } from "./data/repositories/global-search/global-search-impl.repository";
import { HrRepositoryImpl } from "./data/repositories/hr/hr-impl.repository";
import { PersonnelRepositoryImpl } from "./data/repositories/personnel/personnel-impl.repository";
import { ProjectEvaluationRepositoryImpl } from "./data/repositories/project-evaluation/project-evaluation-impl.repository";
import { AgendaRepository } from "./domain/repositories/agenda.repositories";
import { RealCostRepository } from "./domain/repositories/real-cost.repository";
import { AgreementRepository } from "./domain/repositories/agreement.repository";
import { GlobalSearchRepository } from "./domain/repositories/global-search.repositories";
import { HrRepository } from "./domain/repositories/hr.repository";
import { PersonnelRepository } from "./domain/repositories/personnel.repository";
import { ProjectEvaluationRepository } from "./domain/repositories/project-evaluation.repository";
import { getUserRoleUseCaseProvider } from "./domain/use-cases/agenda/get-role.usecase";
import { retrieveAgendaUseCaseProvider } from "./domain/use-cases/agenda/retrieve-agenda.usecase";
import { retrieveRealCostUseCaseProvider } from "./domain/use-cases/real-cost/load-real-cost.usecase";
import { saveMultipliersUseCaseProvider } from "./domain/use-cases/real-cost/save-multipliers.usecase";
import { editNotesUseCaseProvider } from "./domain/use-cases/agreement/edit-notes.usecase";
import { retrieveContractUseCaseProvider } from "./domain/use-cases/agreement/load-agreement.usecase";
import { publishOpportunityUseCaseProvider } from "./domain/use-cases/agreement/publish-opportunity.usecase";
import { saveAgreementUseCaseProvider } from "./domain/use-cases/agreement/save.usecase";
import { updatePETCUseCaseProvider } from "./domain/use-cases/agreement/update-header.usecase";
import { createFakeResourceUseCaseProvider } from "./domain/use-cases/hr/create-fake-resource.usecase";
import { freezeCostsUseCaseProvider } from "./domain/use-cases/hr/freeze-costs.usecase";
import { getAllActivePEUseCaseProvider } from "./domain/use-cases/hr/get-all-active-pe.usecase";
import { getWeekHoursUseCaseProvider } from "./domain/use-cases/hr/get-week-hours.usecase";
import { getAllDisciplinesUseCaseProvider } from "./domain/use-cases/hr/hierarchies/get-all-disciplines.usecase";
import { getAllProfessionalFigureProvider } from "./domain/use-cases/hr/hierarchies/get-all-figures.usecase";
import { getAllGeosUseCaseProvider } from "./domain/use-cases/hr/hierarchies/get-all-geos.usecase";
import { getAllPracticesUseCaseProvider } from "./domain/use-cases/hr/hierarchies/get-all-practices.usecase";
import { retrieveContractResourceModalDataUseCaseProvider } from "./domain/use-cases/hr/retrieve-contract-resource-modal-data.usecase";
import { retrievePfGeosUseCaseProvider } from "./domain/use-cases/hr/retrieve-pfgeos.usecase";
import { deleteAbsenceUseCaseProvider } from "./domain/use-cases/personnel/delete-absence.usecase";
import { retrievePlanningUseCaseProvider } from "./domain/use-cases/personnel/retrieve-planning.usecase";
import { retrieveWeeklyAbsenceUseCaseProvider } from "./domain/use-cases/personnel/retrieve-weekly-absence.usecase";
import { retrieveWeeksListAbsenceProvider } from "./domain/use-cases/personnel/retrieve-weeks-list-absence.usecase";
import { closeProjectEvaluationUseCaseProvider } from "./domain/use-cases/project-evaluation/close-project-evaluation.usecase";
import { exportDisciplineReportUseCaseProvider } from "./domain/use-cases/project-evaluation/export-discipline-report.usecase";
import { exportOpenProjectReportUseCaseProvider } from "./domain/use-cases/project-evaluation/export-open-project-report.usecase"; // Nuovo import
import { exportPracticeReportUseCaseProvider } from "./domain/use-cases/project-evaluation/export-practice-report.usecase";
import { exportProjectReportUseCaseProvider } from "./domain/use-cases/project-evaluation/export-project-report.usecase";
import { exportResourceReportUseCaseProvider } from "./domain/use-cases/project-evaluation/export-resource-report.usecase";
import { exportResourcesByDisciplineReportUseCaseProvider } from "./domain/use-cases/project-evaluation/export-resources-by-discipline-report.usecase";
import { getProjectEvaluationUseCaseProvider } from "./domain/use-cases/project-evaluation/get-project-evaluation.usecase";
import { saveProjectEvaluationUseCaseProvider } from "./domain/use-cases/project-evaluation/save-project-evaluation.usecase";
import { searchResourcesUseCaseProvider } from "./domain/use-cases/project-evaluation/search-resources.usecase";
import { changeStatusUseCaseProvider } from "./domain/use-cases/proposal/change-status.usecase";
import { retrieveProposalUseCaseProvider } from "./domain/use-cases/proposal/retrieve-proposal.usecase";
import { createWorkloadFromOpportunityUseCaseProvider } from "./domain/use-cases/workload/create-workload.usecase";
import { retrieveHeaderInfoUseCaseProvider } from "./domain/use-cases/workload/retrieve-workload.usecase";

// Registra tutti i locali possibili
registerLocaleData(localeIt, "it-IT");
registerLocaleData(localeIt, "it");
registerLocaleData(localeEn, "en-US");
registerLocaleData(localeEn, "en");
registerLocaleData(localeBr, "pt-BR");
registerLocaleData(localeBr, "pt");

const localeFactory = () => {
    const browserLocale = navigator.language;
    console.log("Browser locale detected:", browserLocale);

    // Mappatura delle localizzazioni
    const localeMapping: { [key: string]: string } = {
        it: "it-IT",
        "it-IT": "it-IT",
        en: "en-US",
        "en-US": "en-US",
        pt: "pt-BR",
        "pt-BR": "pt-BR",
        "pt-PT": "pt-BR",
    };

    const mappedLocale =
        localeMapping[browserLocale] ||
        localeMapping[browserLocale.split("-")[0]] ||
        "en-US";

    console.log("Mapped locale:", mappedLocale);
    return mappedLocale;
};

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(routes, withRouterConfig({ onSameUrlNavigation: "reload" })),
        provideHttpClient(withInterceptorsFromDi(), withFetch()),
        // {provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy},
        { provide: AgendaRepository, useClass: AgendaRepositoryImpl },
        { provide: HrRepository, useClass: HrRepositoryImpl },
        { provide: AgreementRepository, useClass: AgreementRepositoryImpl },
        { provide: GlobalSearchRepository, useClass: GlobalSearchRepositoryImpl },
        { provide: PersonnelRepository, useClass: PersonnelRepositoryImpl },
        { provide: ProjectEvaluationRepository, useClass: ProjectEvaluationRepositoryImpl },
        { provide: RealCostRepository, useClass: RealCostRepositoryImpl },
        { provide: LOCALE_ID, useFactory: localeFactory },
        retrieveHeaderInfoUseCaseProvider,
        retrieveAgendaUseCaseProvider,
        retrieveRealCostUseCaseProvider,
        saveMultipliersUseCaseProvider,
        retrieveProposalUseCaseProvider,
        retrieveContractUseCaseProvider,
        getAllProfessionalFigureProvider,
        getAllGeosUseCaseProvider,
        getAllPracticesUseCaseProvider,
        getAllDisciplinesUseCaseProvider,
        getWeekHoursUseCaseProvider,
        updatePETCUseCaseProvider,
        getAllActivePEUseCaseProvider,
        editNotesUseCaseProvider,
        retrieveContractResourceModalDataUseCaseProvider,
        changeStatusUseCaseProvider,
        retrievePfGeosUseCaseProvider,
        retrievePlanningUseCaseProvider,
        retrieveWeeksListAbsenceProvider,
        deleteAbsenceUseCaseProvider,
        retrieveWeeklyAbsenceUseCaseProvider,
        createFakeResourceUseCaseProvider,
        freezeCostsUseCaseProvider,
        createWorkloadFromOpportunityUseCaseProvider,
        saveAgreementUseCaseProvider,
        getUserRoleUseCaseProvider,
        publishOpportunityUseCaseProvider,
        getProjectEvaluationUseCaseProvider,
        searchResourcesUseCaseProvider,
        saveProjectEvaluationUseCaseProvider,
        closeProjectEvaluationUseCaseProvider,
        exportDisciplineReportUseCaseProvider,
        exportPracticeReportUseCaseProvider,
        exportResourceReportUseCaseProvider,
        exportResourcesByDisciplineReportUseCaseProvider,
        exportProjectReportUseCaseProvider,
        exportOpenProjectReportUseCaseProvider,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        { provide: LocationStrategy, useClass: HashLocationStrategy },
    ],
};
